/**
 * Renames and transfers in shared folders run in the background (`transfers.py`): the API answers
 * 202 `{operationId}` and the client polls `GET /v1/shares/{id}/operations/{operationId}` until the
 * operation has `succeeded` or `failed`.
 */
import {shareErrorStatus} from './api'
import {isKnownShareErrorCode, SHARE_ERROR_MESSAGES, shareErrorMessage} from './errors'
import {getShareOperation, ShareOperation} from './shareFilesApi'

/** The operation ended as `failed`. `operation` says what was (and wasn't) copied. */
export class ShareOperationError extends Error {
  readonly operation: ShareOperation
  constructor(operation: ShareOperation) {
    super(`The operation failed${operation.error ? ` (${operation.error})` : ''}.`)
    this.name = 'ShareOperationError'
    this.operation = operation
  }
  /** The error code the worker recorded, if any. */
  get code(): string | undefined {
    return this.operation.error
  }
}

/** Polling gave up while the operation was still queued or running. It may still finish. */
export class ShareOperationTimeoutError<T = ShareOperation> extends Error {
  readonly operation: T | undefined
  constructor(operation: T | undefined) {
    super('The operation is still running.')
    this.name = 'ShareOperationTimeoutError'
    this.operation = operation
  }
}

export interface PollOperationOptions<T> {
  /** Called with each answer while the operation runs (for `done` of `objects`). */
  onProgress?: (operation: T) => void
  /** First wait between polls; doubles up to {@link maxIntervalMs}. */
  intervalMs?: number
  maxIntervalMs?: number
  /** Stop polling after this long; the operation itself is not cancelled. */
  timeoutMs?: number
}

export type WaitForShareOperationOptions = PollOperationOptions<ShareOperation>

/**
 * How operations are polled unless a caller says otherwise. Tests shorten the intervals. The worker
 * is a Lambda (at most 15 minutes) and transfers are capped at 20 GB, so after 20 minutes something
 * is wrong.
 */
export const shareOperationPolling = {
  intervalMs: 1000,
  maxIntervalMs: 5000,
  timeoutMs: 20 * 60 * 1000,
}
/** Network blips while polling are retried; this many in a row end the wait. */
const MAX_POLL_FAILURES = 3

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Reads a background operation with `read` until it has `succeeded` or `failed`, and returns that
 * last answer. Rejects with {@link ShareOperationTimeoutError} when it takes longer than
 * `timeoutMs`, and with the API's error when the operation is gone (any 4xx) or the server failed
 * three times in a row.
 */
export async function pollOperation<T extends {status?: string}>(
  read: () => Promise<T>,
  options: PollOperationOptions<T> & {intervalMs: number; maxIntervalMs: number; timeoutMs: number}
): Promise<T> {
  const {intervalMs, maxIntervalMs, timeoutMs} = options
  const deadline = Date.now() + timeoutMs
  let wait = intervalMs
  let failures = 0
  let last: T | undefined
  for (;;) {
    try {
      last = await read()
      failures = 0
    } catch (error) {
      const status = shareErrorStatus(error)
      // The share, or the operation, is gone for this user: nothing to wait for.
      if (status !== undefined && status < 500) {
        throw error
      }
      failures += 1
      if (failures >= MAX_POLL_FAILURES) {
        throw error
      }
    }
    if (last?.status === 'succeeded' || last?.status === 'failed') {
      return last
    }
    if (last) {
      options.onProgress?.(last)
    }
    if (Date.now() >= deadline) {
      throw new ShareOperationTimeoutError<T>(last)
    }
    await sleep(wait)
    wait = Math.min(wait * 2, maxIntervalMs)
  }
}

/**
 * Polls an operation until it ends. Resolves with the operation when it `succeeded`; rejects with
 * {@link ShareOperationError} when it `failed`, with {@link ShareOperationTimeoutError} when it
 * takes too long, and with the API's error when the share or the operation is gone (403/404).
 */
export async function waitForShareOperation(
  shareId: string,
  operationId: string,
  options: WaitForShareOperationOptions = {}
): Promise<ShareOperation> {
  const last = await pollOperation(() => getShareOperation(shareId, operationId), {
    ...shareOperationPolling,
    ...options,
  })
  if (last.status === 'failed') {
    throw new ShareOperationError(last)
  }
  return last
}

/** A key from an operation (relative to its owner's space) shown relative to `base`, if inside. */
export function operationPathFrom(key: string, base: string): string {
  return base && key.startsWith(base) ? key.slice(base.length) : key
}

/**
 * Why a write in a shared folder failed, for a toast: a failed background operation (by the code the
 * worker recorded), polling that gave up, or an API error (by its `code`, see `errors.ts`).
 */
export function shareOperationErrorMessage(error: unknown): string {
  if (error instanceof ShareOperationError) {
    const code = error.code
    return isKnownShareErrorCode(code)
      ? SHARE_ERROR_MESSAGES[code].message
      : SHARE_ERROR_MESSAGES.internal_error.message
  }
  if (error instanceof ShareOperationTimeoutError) {
    return "It's taking longer than expected. Refresh the folder in a few minutes to see the result."
  }
  return shareErrorMessage(error)
}
